import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from 'modules/state-mgmt/store';
import { fetchWorkerDrugTestingSchedules, fetchWorkerThirdPartyDrugTestResults } from 'modules/state-mgmt/workerData';
import { prettifyErrorResponse } from 'utils';
import { IWorkerDrugTestingSchedule, IWorkerThirdPartyDrugTestResult } from 'models/drugTesting';

import {
  IAppointmentCounts,
  IDrugTestAppointment,
  IThirdPartyTestResult,
  TabIndex,
} from '../types';

const PAGE_SIZE = 2;

const DRUG_TESTING_RESULT_LABELS: Record<number, string> = {
  0: '—',
  1: 'Negative',
  2: 'Non-Conclusive',
  3: 'Invalid',
  4: 'Sent to Lab',
  5: 'No Show',
  6: 'Cancelled by Worker',
  7: 'BAT Pass',
  8: 'BAT Fail',
  9: 'Positive',
  10: 'Lost in Transit',
  11: 'Leak During Transit',
  12: 'Fatal Flaw',
  13: 'Other',
};

const formatAppointmentDate = (dateString?: string | null): string => {
  if (!dateString) return '--';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const transformAppointment = (item: IWorkerDrugTestingSchedule): IDrugTestAppointment => ({
  id: item.id,
  facilityName: item.facilityName || '--',
  appointmentDate: formatAppointmentDate(item.testScheduledForDate),
  companyName: item.invoiceToCompanyName || '--',
  projectName: item.projectName || '--',
  typeName: item.drugTestingType || '--',
  panelName: item.panelName || '--',
  reason: item.reason || '--',
  testScheduledForDate: item.testScheduledForDate,
  workerId: item.workerId,
  projectId: item.projectId,
  facilityId: item.facilityId,
  drugTestingTypeId: item.drugTestingTypeId,
  panelId: item.panelId,
  panelTests: item.panelTests || [],
  status: item.status,
  actionStatus: item.actionStatus,
  drugTestingScheduleResult: item.drugTestingScheduleResult,
  // Raw fields backing the extended appointment view (Address / Substance / Result / BAT / Details).
  facilityAddress: item.facilityAddress,
  resultSentToLabTestSubstances: item.resultSentToLabTestSubstances || [],
  batTestResult: item.batTestResult,
  batConfirmationTestResult: item.batConfirmationTestResult,
  drugTestingScheduleInvalidResultType: item.drugTestingScheduleInvalidResultType,
  invalidResultTypeFreeText: item.invalidResultTypeFreeText,
  resultTypeFreeText: item.resultTypeFreeText,
});

const transformThirdPartyResult = (item: IWorkerThirdPartyDrugTestResult): IThirdPartyTestResult => ({
  id: item.id,
  facilityName: item.clinicName || '--',
  appointmentDate: formatAppointmentDate(item.testedAt),
  companyName: '--',
  projectName: item.project?.name || '--',
  typeName: item.type?.name || '--',
  panelName: item.panel?.name || '--',
  reason: item.reason || '--',
  testResult: DRUG_TESTING_RESULT_LABELS[item.result] ?? '--',
  resultStatus: item.status,
  substance: (item.panel?.tests || []).map(t => t.name).join(', ') || '--',
  attachments: (item.documents || []).map(doc => ({
    id: doc.id,
    fileName: doc.displayName || doc.fileName,
    url: doc.url,
  })),
  // Raw fields preserved for edit prefill.
  projectId: item.project?.id,
  panelId: item.panel?.id,
  drugTestingTypeId: item.type?.id,
  resultValue: item.result,
  testedAt: item.testedAt,
});

export const useDrugTestingList = () => {
  const dispatch = useAppDispatch();

  const { workerId, isWorker, languageCode, drugTestingSchedules, thirdPartyDrugTestResults } = useAppSelector(
    state => ({
      // Direct worker dashboard uses user.workerId; onboarding.id is set when an admin acts on a worker.
      workerId: state.onboarding.id || state.user?.workerId,
      isWorker: state.user?.isWorker,
      languageCode: state.app?.languageCode,
      drugTestingSchedules: state.workerData.drugTestingSchedules,
      thirdPartyDrugTestResults: state.workerData.thirdPartyDrugTestResults,
    }),
  );

  const [activeTab, setActiveTab] = useState<TabIndex>(TabIndex.APPOINTMENTS);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Tracks whether the initial mount fetch has already run so tab/page changes
  // don't double-fetch on the first render.
  const initialized = useRef(false);

  const fetchAppointments = useCallback(
    (pageNum: number): Promise<void> => {
      if (!isWorker && !workerId) return Promise.resolve();
      return dispatch(
        fetchWorkerDrugTestingSchedules({
          workerId,
          pageNumber: pageNum,
          pageSize: PAGE_SIZE,
          languageCode: languageCode || 'en',
        }),
      )
        .unwrap()
        .catch(err => {
          const errorMessage = prettifyErrorResponse(err);
          setError(errorMessage);
          enqueueSnackbar(errorMessage, { variant: 'error' });
        });
    },
    [dispatch, workerId, isWorker, languageCode],
  );

  const fetchThirdPartyResults = useCallback(
    (pageNum: number): Promise<void> => {
      if (!isWorker && !workerId) return Promise.resolve();
      return dispatch(
        fetchWorkerThirdPartyDrugTestResults({
          workerId,
          pageNumber: pageNum,
          pageSize: PAGE_SIZE,
          languageCode: languageCode || 'en',
        }),
      )
        .unwrap()
        .catch(err => {
          const errorMessage = prettifyErrorResponse(err);
          setError(errorMessage);
          enqueueSnackbar(errorMessage, { variant: 'error' });
        });
    },
    [dispatch, workerId, isWorker, languageCode],
  );

  // On mount (and whenever workerId/language changes): fetch both tabs so counts
  // are always fresh — never read from stale Redux-persist cache.
  useEffect(() => {
    initialized.current = false;
    setIsLoading(true);
    setError(null);
    Promise.all([fetchAppointments(1), fetchThirdPartyResults(1)]).finally(() => setIsLoading(false));
  }, [fetchAppointments, fetchThirdPartyResults]);

  // On tab or page change: fetch only the newly active tab's data.
  // Skip on initial render since the mount effect above already handles it.
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      return;
    }
    setIsLoading(true);
    setError(null);
    const fetch = activeTab === TabIndex.APPOINTMENTS ? fetchAppointments(page) : fetchThirdPartyResults(page);
    fetch?.finally(() => setIsLoading(false));
  }, [activeTab, page, fetchAppointments, fetchThirdPartyResults]);

  const appointments: IDrugTestAppointment[] = useMemo(() => {
    if (activeTab !== TabIndex.APPOINTMENTS) return [];
    return (drugTestingSchedules?.items || []).map(transformAppointment);
  }, [activeTab, drugTestingSchedules]);

  const testResults: IThirdPartyTestResult[] = useMemo(() => {
    if (activeTab !== TabIndex.THIRD_PARTY_RESULTS) return [];
    return (thirdPartyDrugTestResults?.items || []).map(transformThirdPartyResult);
  }, [activeTab, thirdPartyDrugTestResults]);

  const counts: IAppointmentCounts = useMemo(
    () => ({
      appointments: drugTestingSchedules?.totalResults || 0,
      thirdPartyResults: thirdPartyDrugTestResults?.totalResults || 0,
    }),
    [drugTestingSchedules, thirdPartyDrugTestResults],
  );

  const handleTabChange = (_event: React.SyntheticEvent | unknown, newValue: number) => {
    setActiveTab(newValue as TabIndex);
    setPage(1);
  };

  const handlePageChange = (value: number) => {
    setPage(value);
  };

  const totalResults =
    activeTab === TabIndex.APPOINTMENTS
      ? drugTestingSchedules?.totalResults || 0
      : thirdPartyDrugTestResults?.totalResults || 0;

  const paginationInfo = {
    totalResults,
    pageSize: PAGE_SIZE,
  };

  return {
    appointments,
    testResults,
    counts,
    activeTab,
    page,
    paginationInfo,
    isLoading,
    error,
    handleTabChange,
    handlePageChange,
    refetch: () => {
      setIsLoading(true);
      setError(null);
      fetchAppointments(page)?.finally(() => setIsLoading(false));
    },
    refetchThirdPartyResults: () => {
      setIsLoading(true);
      setError(null);
      fetchThirdPartyResults(page)?.finally(() => setIsLoading(false));
    },
  };
};
